import { defineStore } from "pinia";

import type { Snackbar } from "@/Modules/App/Models/Domain/Snackbar";
import type { AppState, Icon } from "./Models/Domain/AppState";
import type { LocalNotification } from "@/Modules/BrowserNotifications/interfaces";
import { StateNamespace } from "./Models/State/StateNamespace";

import GetCancelReasonsActionHandler from "@/Modules/Choice/ActionHandlers/GetCancelReasons";

import { useSubscriptionStore } from "@/Modules/Subscription/Store";
import { useQuestionnaireStore } from "@/Modules/Questionnaire/Store";
import { useUserLegalAgreementStore } from "@/Modules/LegalAgreement/Store";

import CheckUpdateActionHandler from "./ActionHandlers/CheckUpdates";
import ClearStoreActionHandler from "./ActionHandlers/ClearStores";
import CheckConnectionActionHandler from "./ActionHandlers/CheckConnection";
import NotificateActionHander from "./ActionHandlers/Notificate";
import IsNeededPreviewActionHandler from "./ActionHandlers/IsNeededPreview";
import UpdatePreviewDateActionHandler from "./ActionHandlers/UpdatePreviewDate";
import CheckAppStatusActionHandler from "./ActionHandlers/CheckAppStatus";

import { config } from "@/Common/envConfig";

import agentDetector from "@/Utils/AgentDetection";
import { parseOrValidateInt } from "@/Utils/Numbers";
import { isApp } from "@/Utils/Ionic";

const appState: AppState = {
  isLoading: false,
  isSideMenuOpened: false,
  notification: null,
  snackbar: undefined,
  endIconSidebar: null,
  isOnline: true,
  viewportHeight: 0,
  viewportWidth: 0,
  device: agentDetector(),
  cancelReasons: [],
  needUpdate: false,
  isForceUpdate: false,
  versionRequirements: undefined,
  isForeground: true,
  isKeyboardOpen: false,
  isVisibleNavigation: true,
};

export const useAppStore = defineStore(StateNamespace.APP, {
  state: () => ({ ...appState }),
  getters: {
    getPendingNotifications(): number {
      const subscriptionStore = useSubscriptionStore();
      const questionnaireStore = useQuestionnaireStore();
      const userLegalAgreementsStore = useUserLegalAgreementStore();

      const subscriptionNotification = subscriptionStore.getTotalPendingNotifications;
      const questionnaireNotification = questionnaireStore.getTotalPendingNotifications;
      const legalAgreementsNotification = userLegalAgreementsStore.getTotalPendingNotifications;

      return subscriptionNotification + questionnaireNotification + legalAgreementsNotification;
    },

    isMobileViewport(): boolean {
      if (this.viewportWidth === 0) return false;

      return this.viewportWidth <= config.breakpointDesktop;
    },
  },

  actions: {
    checkAppStatus() {
      return CheckAppStatusActionHandler();
    },

    clearStores(logout = true) {
      const handler = new ClearStoreActionHandler();

      return handler.handle(logout);
    },

    hasConnection(): Promise<boolean> {
      const handler = new CheckConnectionActionHandler();

      return handler.handle();
    },

    checkUpdate() {
      const handler = new CheckUpdateActionHandler(this);

      handler.handle();
    },

    updateStatusOnline(isOnline: boolean) {
      this.isOnline = isOnline;
    },

    closeSideMenu() {
      this.isSideMenuOpened = false;
    },

    openSideMenu() {
      this.isSideMenuOpened = true;
    },

    toggleSideMenu() {
      this.isSideMenuOpened = !this.isSideMenuOpened || true;
    },

    showSnackbar({ message, messageParameters, type, duration = 5000 }: Snackbar) {
      this.snackbar = { message, messageParameters, type };

      setTimeout(() => {
        this.snackbar = undefined;
      }, duration);
    },

    cleanEndIconSidebar() {
      this.endIconSidebar = null;
    },

    setEndIconSidebar(icon: Icon) {
      this.endIconSidebar = icon;
    },

    updateViewport(width: number, height: number) {
      this.viewportHeight = height;
      this.viewportWidth = width;
    },

    async getCancelReasons() {
      const getCancelReasonsActionHandler = new GetCancelReasonsActionHandler(this);

      await getCancelReasonsActionHandler.handle();
    },

    updateAppStatus(isActive: boolean) {
      this.isForeground = isActive;
    },

    notificate(notification: LocalNotification) {
      const handler = new NotificateActionHander();

      handler.handle(notification);
    },

    isNeededPreview(): boolean {
      const handler = new IsNeededPreviewActionHandler();

      return handler.handle();
    },

    updatePreviewDate(): number {
      const handler = new UpdatePreviewDateActionHandler();

      return handler.handle();
    },

    updateKeyboardStatus(isOpen: boolean) {
      this.isKeyboardOpen = isOpen;
    },

    showNavigation() {
      this.isVisibleNavigation = true;
    },

    hideNavigation() {
      this.isVisibleNavigation = false;
    },

    getAppDisplay(): { top: number; bottom: number; height: number; width: number } {
      if (!isApp()) {
        return { top: 0, bottom: 0, height: window.innerHeight, width: window.innerWidth };
      }

      const root = document.documentElement;
      const styles = getComputedStyle(root);
      const safeAreaBottom = isApp()
        ? parseOrValidateInt(styles.getPropertyValue("--ion-safe-area-bottom"), 0)
        : 0;
      const safeAreaTop = isApp() ? parseOrValidateInt(styles.getPropertyValue("--ion-safe-area-top"), 0) : 0;

      let viewHeight = window.innerHeight;

      viewHeight -= safeAreaBottom + safeAreaTop;

      return {
        top: safeAreaTop,
        bottom: safeAreaBottom,
        height: viewHeight,
        width: window.outerWidth,
      };
    },
  },
});

export type AppStore = ReturnType<typeof useAppStore>;

export default useAppStore;
